/**
 * A spot for a chord: a line of the text, and how many characters into its lyrics (the line without its chords).
 * When chords are already played over that spot, `nth` says where among them: 0 is before the first. Without it,
 * it's after them all.
 */
export type Place = {
    line: number;
    at: number;
    nth?: number;
};

/** A chord on the sheet: where it is, and which one of the chords played over that spot. */
export type ChordRef = Place & { nth: number };

/** A line split into its lyrics and the chords in it, each with where it's played in the lyrics. */
type LineChords = {
    lyrics: string;
    chords: { at: number; chord: string }[];
};

/** Moves a chord to another spot, or to another place among the chords on its own spot. Nothing else in the text changes. */
export function moveChord(text: string, chord: ChordRef, to: Place): string {
    const name = chordAt(text, chord);

    if (name === undefined) return text;
    if (chord.line !== to.line || chord.at !== to.at) return placeChord(removeChord(text, chord), to, name);

    const count = stackAt(text, to).length;
    const nth = Math.min(to.nth ?? count, count);

    if (nth === chord.nth || nth === chord.nth + 1) return text;

    // Once the chord is taken off, the chords after it move up one.
    return placeChord(removeChord(text, chord), { ...to, nth: nth > chord.nth ? nth - 1 : nth }, name);
}

/** Takes a chord off the sheet. */
export function removeChord(text: string, chord: ChordRef): string {
    return changeLine(text, chord.line, ({ lyrics, chords }) => {
        const index = chords.indexOf(chordsAt(chords, chord.at)[chord.nth]);

        return { lyrics, chords: chords.filter((_, i) => i !== index) };
    });
}

/**
 * Puts a chord on a spot, which can be anywhere in a word. Chords already there stay, and it goes among them where
 * `to.nth` says, or after them all: `[G][D]grace`.
 */
export function placeChord(text: string, to: Place, chord: string): string {
    return changeLine(text, to.line, ({ lyrics, chords }) => {
        const next = to.nth === undefined ? undefined : chordsAt(chords, to.at)[to.nth];
        const index = next ? chords.indexOf(next) : chords.length;

        return { lyrics, chords: chords.toSpliced(index, 0, { at: to.at, chord }) };
    });
}

/** Changes a chord where it is, like giving it a bass note. */
export function changeChord(text: string, chord: ChordRef, change: (name: string) => string): string {
    return changeLine(text, chord.line, ({ lyrics, chords }) => {
        const found = chordsAt(chords, chord.at)[chord.nth];

        return { lyrics, chords: chords.map((other) => (other === found ? { ...other, chord: change(other.chord.trim()) } : other)) };
    });
}

/** The name of a chord, as it's written in the text. */
export function chordAt(text: string, chord: ChordRef): string | undefined {
    return stackAt(text, chord)[chord.nth]?.chord;
}

/** The chords played over a spot in the text. */
function stackAt(text: string, place: Place): LineChords["chords"] {
    const line = text.split("\n")[place.line];

    return line === undefined ? [] : chordsAt(readLine(line).chords, place.at);
}

/** The chords played over a spot. Empty ones, written `[]`, don't count, just like on the sheet. */
function chordsAt(chords: LineChords["chords"], at: number): LineChords["chords"] {
    return chords.filter((chord) => chord.at === at && chord.chord.trim());
}

/** Changes one line of the text, keeping its line ending. */
function changeLine(text: string, index: number, change: (line: LineChords) => LineChords): string {
    const all = text.split("\n");

    if (all[index] === undefined) return text;

    const ending = all[index].endsWith("\r") ? "\r" : "";

    all[index] = writeLine(change(readLine(all[index].slice(0, all[index].length - ending.length)))) + ending;

    return all.join("\n");
}

function readLine(line: string): LineChords {
    const parts = line.split(/\[([^\]]*)\]/);
    const chords: LineChords["chords"] = [];
    let lyrics = parts[0];

    for (let i = 1; i < parts.length; i += 2) {
        chords.push({ at: lyrics.length, chord: parts[i] });
        lyrics += parts[i + 1] ?? "";
    }

    return { lyrics, chords };
}

/** Writes a line back, with every chord in front of the lyrics it's played over. A line that didn't change comes out as it was. */
function writeLine({ lyrics, chords }: LineChords): string {
    const sorted = chords.toSorted((a, b) => a.at - b.at);
    let line = "";
    let from = 0;

    for (const { at, chord } of sorted) {
        line += `${lyrics.slice(from, at)}[${chord}]`;
        from = at;
    }

    return line + lyrics.slice(from);
}
